import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ToastMsg from "../../components/ToastMsg";
import PortalPopup from "../../components/PortalPopup";
import LogoutConfirmation from "../../components/LogoutConfirmation";
import ExportModalTables from "../../components/ExportModalTables";
import NewPayment from "../../components/NewPayment";
import Upload from "../../components/Upload";
import NavigationPanel from "../../components/NavigationPanel";
import Breadcrumb from "../../components/Breadcrumb";
import CTAs from "../../components/CTAs";
import Dropdown from "../../components/Dropdown";
import Toggle from "../../components/Toggle";
import Footer from "../../components/Footer";
import Checkbox from "../../components/Checkbox";
import SettingNav from "../../components/settingNav";
import { subscriptionService } from "../../services/subscriptionService";
import { stripeService } from "../../services/stripeService";
import { openBankingService } from "../../services/openBankingService";

// ── Payment method display helpers ──
// Works off the subscription provider (company.subscriptionProvider) first,
// then the payment method returned by getSubscriptionStatus:
//   paymentMethod: { type, brand, last4, expMonth, expYear, wallet, email }
const WALLET_LABELS = {
  apple_pay: "Apple Pay",
  google_pay: "Google Pay",
  samsung_pay: "Samsung Pay",
  link: "Link",
};

const formatExpiry = (pm) =>
  pm?.expMonth && pm?.expYear
    ? `Expires ${String(pm.expMonth).padStart(2, "0")}/${String(pm.expYear).slice(-2)}`
    : null;

const formatCard = (pm) => {
  if (!pm?.last4) return null;
  const brand = pm.brand ? String(pm.brand).toUpperCase() : "CARD";
  return `${brand} •••• ${pm.last4}`;
};

const REVOLUT_STATUS_TEXT = {
  active:    { text: "Billed monthly through Revolut", tone: "ok" },
  pending:   { text: "Waiting for the first payment on Revolut", tone: "warn" },
  overdue:   { text: "Payment overdue. Revolut will retry automatically", tone: "warn" },
  paused:    { text: "Revolut subscription paused", tone: "warn" },
  cancelled: { text: "Revolut subscription cancelled", tone: "error" },
  finished:  { text: "Revolut subscription ended", tone: "error" },
};

const getPaymentMethodDisplay = (subscriptionData) => {
  const pm              = subscriptionData?.paymentMethod || null;
  const provider =
    subscriptionData?.provider ||
    subscriptionData?.company?.subscriptionProvider ||
    (pm?.type === "revolut" ? "revolut" : "stripe");
  const hasSubscription = subscriptionData?.hasSubscription;

  // ── Revolut (Revolut Merchant subscription) ──
  if (provider === "revolut") {
    const state  = subscriptionData?.subscription?.status;
    const status = REVOLUT_STATUS_TEXT[state] || { text: "Billed through Revolut", tone: "ok" };
    const isRevolutPay = pm?.type === "revolut_pay";
    const title = isRevolutPay ? "Revolut Pay" : formatCard(pm) || "Revolut";
    const expiry = !isRevolutPay ? formatExpiry(pm) : null;

    return {
      provider:  "revolut",
      title,
      subtitle:  [status.text, expiry].filter(Boolean).join(" · "),
      tone:      status.tone,
      sublabel:  "Your plan is billed through Revolut.",
      icon:      "assets/revolut.svg",
      iconAlt:   "Revolut",
      fallback:  "R",
      dark:      true,
      badge:     title === "Revolut" ? null : "Revolut",
      canManage: false,
    };
  }

  const managedText = hasSubscription
    ? "Managed through Stripe billing portal"
    : "No active subscription";

  if (!pm) {
    return {
      provider:  "stripe",
      title:     "Card on file",
      subtitle:  managedText,
      tone:      "ok",
      sublabel:  "The card used to pay for your plan.",
      icon:      "assets/stripe.svg",
      iconAlt:   "Stripe",
      badge:     null,
      canManage: true,
    };
  }

  const type = pm.type || (pm.brand || pm.last4 ? "card" : null);

  // ── Revolut Pay through Stripe ──
  if (type === "revolut_pay") {
    return {
      provider:  "stripe",
      title:     "Revolut Pay",
      subtitle:  pm.email ? `${pm.email} · ${managedText}` : managedText,
      tone:      "ok",
      sublabel:  "Your plan is paid through Revolut Pay.",
      icon:      "assets/revolut.svg",
      iconAlt:   "Revolut",
      fallback:  "R",
      dark:      true,
      badge:     null,
      canManage: true,
    };
  }

  if (type === "link") {
    return {
      provider:  "stripe",
      title:     pm.email ? `Link · ${pm.email}` : "Link by Stripe",
      subtitle:  managedText,
      tone:      "ok",
      sublabel:  "The payment method used to pay for your plan.",
      icon:      "assets/stripe.svg",
      iconAlt:   "Stripe Link",
      badge:     null,
      canManage: true,
    };
  }

  if (type === "card") {
    return {
      provider:  "stripe",
      title:     formatCard(pm) || (pm.brand ? String(pm.brand).toUpperCase() : "Card on file"),
      subtitle:  [managedText, formatExpiry(pm)].filter(Boolean).join(" · "),
      tone:      "ok",
      sublabel:  "The card used to pay for your plan.",
      icon:      "assets/stripe.svg",
      iconAlt:   "Stripe",
      badge:     pm.wallet ? WALLET_LABELS[pm.wallet] || null : null,
      canManage: true,
    };
  }

  const prettyType = String(type || "Payment method")
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
  return {
    provider:  "stripe",
    title:     pm.last4 ? `${prettyType} •••• ${pm.last4}` : prettyType,
    subtitle:  managedText,
    tone:      "ok",
    sublabel:  "The payment method used to pay for your plan.",
    icon:      "assets/stripe.svg",
    iconAlt:   "Stripe",
    badge:     null,
    canManage: true,
  };
};

const TONE_COLORS = { ok: "#64748B", warn: "#B45309", error: "#B91C1C" };

// Shown in the Manage modal for Revolut subscriptions (no self-service portal).
// Leave empty to hide the "Contact support" button.
const SUPPORT_EMAIL = "";

const PROVIDER_LABELS = { stripe: "Stripe", paystack: "Paystack", revolut: "Revolut" };

const STATUS_META = {
  active:         { label: "Active",                 color: "#15803D", bg: "#F0FDF4" },
  trialing:       { label: "Trial",                  color: "#1D4ED8", bg: "#EFF6FF" },
  pending:        { label: "Awaiting first payment", color: "#B45309", bg: "#FFFBEB" },
  incomplete:     { label: "Awaiting first payment", color: "#B45309", bg: "#FFFBEB" },
  past_due:       { label: "Payment overdue",        color: "#B45309", bg: "#FFFBEB" },
  overdue:        { label: "Payment overdue",        color: "#B45309", bg: "#FFFBEB" },
  unpaid:         { label: "Unpaid",                 color: "#B91C1C", bg: "#FEF2F2" },
  paused:         { label: "Paused",                 color: "#475569", bg: "#F1F5F9" },
  "non-renewing": { label: "Ends at period end",     color: "#B45309", bg: "#FFFBEB" },
  canceled:       { label: "Cancelled",              color: "#B91C1C", bg: "#FEF2F2" },
  cancelled:      { label: "Cancelled",              color: "#B91C1C", bg: "#FEF2F2" },
  finished:       { label: "Ended",                  color: "#B91C1C", bg: "#FEF2F2" },
};

// Stripe sends unix seconds, others send ISO strings.
const toDate = (value) => {
  if (!value) return null;
  if (typeof value === "number") return new Date(value < 1e12 ? value * 1000 : value);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

// Pulls a readable message out of axios / fetch-style errors and responses.
const getErrorMessage = (errOrResult, fallback) =>
  errOrResult?.response?.data?.message ||
  errOrResult?.data?.message ||
  (errOrResult?.success === false ? errOrResult?.message : null) ||
  (errOrResult instanceof Error ? errOrResult.message : null) ||
  fallback;

const SettingsBillingPage = () => {
  const navigate = useNavigate();
  const [isToastMsgOpen, setToastMsgOpen] = useState(false);
  const [isLogoutConfirmationOpen, setLogoutConfirmationOpen] = useState(false);
  const [isExportModalTablesOpen, setExportModalTablesOpen] = useState(false);
  const [isNewPayment1Open, setNewPayment1Open] = useState(false);
  const [isCancelModalOpen, setCancelModalOpen] = useState(false);
  const [isManageModalOpen, setManageModalOpen] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState(null);

  const [subscriptionData, setSubscriptionData] = useState(null);
  const [stripeAccountStatus, setStripeAccountStatus] = useState(null);
  const [openBankingStatus, setOpenBankingStatus] = useState(null);
  const [obBanks, setObBanks] = useState([]);
  const [obFormData, setObFormData] = useState({
    accountHolderType: "individual",
    bankId: "",
    accountNumber: "",
    accountType: "",
    initials: "",
    familyName: "",
    idNumber: "",
    idCountry: "ZA",
    phone: "",
    email: "",
    businessName: "",
    registrationNumber: "",
    registrationCountry: "ZA",
  });
  const [obVerifying, setObVerifying] = useState(false);
  const [obVerifyMessage, setObVerifyMessage] = useState(null); // { type: 'pending' | 'review' | 'failed', text }
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toastMessage, setToastMessage] = useState("");

  // ── Reveal full (unmasked) account number ──
  const [revealedAccountNumber, setRevealedAccountNumber] = useState(null);
  const [revealing, setRevealing] = useState(false);

  // ── Payment method icon fallback (if assets/revolut.svg is missing) ──
  const [pmIconFailed, setPmIconFailed] = useState(false);

  const showToast = (msg) => {
    setToastMessage(msg);
    setToastMsgOpen(true);
  };

  const loadBillingData = async () => {
    try {
      setLoading(true);
      const [subData, stripeData, obData] = await Promise.all([
        subscriptionService.getSubscriptionStatus(),
        stripeService.getAccountStatus(),
        openBankingService
          .getStatus()
          .catch(() => ({ status: "disconnected", account: null })),
      ]);
      setSubscriptionData(subData);
      setStripeAccountStatus(stripeData);
      setOpenBankingStatus(obData);
      setPmIconFailed(false);
    } catch (err) {
      console.error("Error loading billing data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (params.get("success") === "true") {
      window.history.replaceState({}, "", window.location.pathname);
      stripeService.getAccountStatus()
        .then(data => {
          setStripeAccountStatus(data);
          showToast("Stripe account connected successfully!");
        })
        .catch(err => console.error("Failed to sync Stripe status:", err));
    }

    if (params.get("refresh") === "true") {
      window.history.replaceState({}, "", window.location.pathname);
      stripeService.getOnboardingLink()
        .then(result => { window.location.href = result.onboardingUrl; })
        .catch(() => showToast("Failed to resume Stripe setup. Please try again."));
    }

    // Open Banking (BAV) has no redirect flow — verification happens
    // synchronously via the form, so there's nothing to handle here.
    loadBillingData();

    openBankingService.getBanks()
      .then(setObBanks)
      .catch((err) => console.error("Failed to load bank list:", err));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStripeConnect = async () => {
    try {
      setActionLoading(true);

      if (!stripeAccountStatus?.connected) {
        const result = await stripeService.createConnectAccount();
        console.log("createConnectAccount result:", result);
        if (!result.onboardingUrl) throw new Error("No onboarding URL returned");
        window.location.href = result.onboardingUrl;

      } else if (!stripeAccountStatus?.onboardingComplete) {
        const result = await stripeService.getOnboardingLink();
        if (!result.onboardingUrl) throw new Error("No onboarding URL returned");
        window.location.href = result.onboardingUrl;

      } else {
        const result = await stripeService.getDashboardLink();
        window.open(result.dashboardUrl, "_blank");
      }
    } catch (err) {
      console.error("Stripe Connect error:", err);
      showToast(err.message || "Failed to connect to Stripe. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleObInputChange = (field, value) => {
    setObFormData((prev) => ({ ...prev, [field]: value }));
  };

 const handleSaveAccount = async (e) => {
  if (e) e.preventDefault();

  const payload = {
    accountHolderType: obFormData.accountHolderType,
    accountNumber:     obFormData.accountNumber,
    bankId:            obFormData.bankId,
    accountType:       obFormData.accountType || undefined,
    ...(obFormData.accountHolderType === "business"
      ? {
          businessName:         obFormData.businessName,
          registrationNumber:   obFormData.registrationNumber,
          registrationCountry:  obFormData.registrationCountry,
        }
      : {
          initials:   obFormData.initials,
          familyName: obFormData.familyName,
          idNumber:   obFormData.idNumber,
          idCountry:  obFormData.idCountry,
          phone:      obFormData.phone || undefined,
          email:      obFormData.email || undefined,
        }),
  };

  try {
    setObVerifying(true);
    setObVerifyMessage(null);

    const result = await openBankingService.saveAccount(payload);

    if (result.status === "unverified") {
      showToast("Bank details saved. Verification will run once available.");
      const status = await openBankingService.getStatus();
      setOpenBankingStatus(status);
      setObVerifyMessage(null);
      setRevealedAccountNumber(null);
    }
  } catch (err) {
    console.error("Open Banking save error:", err);
    setObVerifyMessage({ type: "failed", text: err.message || "Failed to save bank account. Please try again." });
  } finally {
    setObVerifying(false);
  }
};

  const handleRemoveAccount = async () => {
    if (!window.confirm("Remove this payout account? You'll need to verify a new one before payouts can continue.")) {
      return;
    }
    try {
      setActionLoading(true);
      await openBankingService.removeAccount();
      setOpenBankingStatus({ status: "disconnected", account: null });
      setRevealedAccountNumber(null);
      showToast("Payout account removed.");
    } catch (err) {
      console.error("Open Banking remove error:", err);
      showToast("Failed to remove the account. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  // ── Reveal / hide the full account number on demand ──
  // Not fetched as part of getStatus()/loadBillingData() — only pulled
  // when the owner explicitly asks, so the unmasked number doesn't sit in
  // state any longer than necessary.
  const handleRevealAccountNumber = async () => {
    if (revealedAccountNumber) {
      setRevealedAccountNumber(null); // toggle back to masked view
      return;
    }
    try {
      setRevealing(true);
      const number = await openBankingService.revealAccountNumber();
      setRevealedAccountNumber(number);
    } catch (err) {
      console.error("Error revealing account number:", err);
      showToast("Failed to reveal account number. Please try again.");
    } finally {
      setRevealing(false);
    }
  };

  // Opens the provider's billing page (Stripe portal / Paystack card link).
  // Errors stay inside the Manage modal instead of failing silently.
  const openBillingPortal = async () => {
    setPortalError(null);
    setPortalLoading(true);
    try {
      const result = await subscriptionService.createPortalSession();
      const url = result?.url || result?.data?.url;
      if (!url) {
        throw new Error(getErrorMessage(result, "The billing page couldn't be opened. Please try again."));
      }
      window.location.href = url;
    } catch (err) {
      console.error("Error opening billing portal:", err);
      setPortalError(getErrorMessage(err, "The billing page couldn't be opened. Please try again."));
      setPortalLoading(false);
    }
  };

  const openManageModal = () => {
    setPortalError(null);
    setManageModalOpen(true);
  };
  const closeManageModal = () => {
    if (portalLoading) return;
    setManageModalOpen(false);
  };
  const startCancelFromManage = () => {
    setManageModalOpen(false);
    setCancelModalOpen(true);
  };

  const handleCancelSubscription = async () => {
    try {
      setActionLoading(true);
      await subscriptionService.cancelSubscription();
      showToast("Subscription canceled successfully.");
      setCancelModalOpen(false);
      await loadBillingData();
    } catch (err) {
      console.error("Error canceling subscription:", err);
      showToast("Failed to cancel subscription. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  const openToastMsg = useCallback(() => setToastMsgOpen(true), []);
  const closeToastMsg = useCallback(() => setToastMsgOpen(false), []);
  const openLogoutConfirmation = useCallback(() => setLogoutConfirmationOpen(true), []);
  const closeLogoutConfirmation = useCallback(() => setLogoutConfirmationOpen(false), []);
  const openExportModalTables = useCallback(() => setExportModalTablesOpen(true), []);
  const closeExportModalTables = useCallback(() => setExportModalTablesOpen(false), []);
  const openNewPayment1 = useCallback(() => setNewPayment1Open(true), []);
  const closeNewPayment1 = useCallback(() => setNewPayment1Open(false), []);
  const openCancelModal = useCallback(() => setCancelModalOpen(true), []);
  const closeCancelModal = useCallback(() => setCancelModalOpen(false), []);

  const handleToggle = (state) => {
    console.log("Toggle state is:", state);
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getPlanName = (accountType) =>
    accountType === "professional_services"
      ? "Professional Services plan"
      : "Business plan";

  const getPlanDescription = (accountType) =>
    accountType === "professional_services"
      ? "Perfect for professional service providers."
      : "Our most popular plan for small teams.";

  const getPlanPrice = (accountType) =>
    accountType === "professional_services" ? "£29.99" : "£49.99";

  // Prefer the real billed amount/currency (e.g. a ZAR Revolut plan) over the
  // hardcoded GBP list price. amount is in minor units.
  const getDisplayPrice = () => {
    const sub = subscriptionData?.subscription;
    if (sub?.amount != null && sub?.currency) {
      try {
        return new Intl.NumberFormat("en-GB", {
          style: "currency",
          currency: String(sub.currency).toUpperCase(),
        }).format(sub.amount / 100);
      } catch {
        /* fall through to list price */
      }
    }
    return subscriptionData?.company
      ? getPlanPrice(subscriptionData.company.accountType)
      : "£49.99";
  };

  if (loading) {
    return (
      <div className="w-full relative bg-neutrals-neutral-50 min-h-screen flex items-center justify-center">
        <div className="text-lg font-semibold">Loading billing information...</div>
      </div>
    );
  }

  const pmDisplay        = getPaymentMethodDisplay(subscriptionData);
  const providerKey      =
    subscriptionData?.provider ||
    subscriptionData?.company?.subscriptionProvider ||
    pmDisplay.provider;
  const providerLabel    = PROVIDER_LABELS[providerKey] || "Stripe";
  const statusKey        =
    subscriptionData?.subscription?.status ||
    subscriptionData?.company?.subscriptionStatus ||
    null;
  const statusMeta       = STATUS_META[statusKey] || null;
  const nextPaymentDate  =
    toDate(subscriptionData?.company?.nextBillingDate) ||
    toDate(subscriptionData?.subscription?.currentPeriodEnd);
  const isCancelled      = ["canceled", "cancelled", "finished"].includes(statusKey);
  const planName         = subscriptionData?.company
    ? getPlanName(subscriptionData.company.accountType)
    : "Business plan";
  const isRevolutSub     = pmDisplay.provider === "revolut";

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&display=swap');

        html, body { background: rgb(241, 245, 249) !important; }

        .dd-root * { box-sizing: border-box; }
        .dd-root {
          font-family: 'DM Sans', sans-serif;
          background: rgb(241, 245, 249) !important;
          min-height: 100vh;
        }

        .dd-card {
          background: #ffffff;
          border: 1px solid #E2E8F0;
          border-radius: 16px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 2px 12px rgba(0,0,0,0.04);
          transition: box-shadow 0.2s ease;
          position: relative;
          overflow: hidden;
        }

        .dd-section-divider {
          border: none;
          border-top: 1px solid #E2E8F0;
          margin: 0;
        }

        .dd-section-label {
          font-size: 14px;
          font-weight: 700;
          color: #0F172A;
          margin: 0 0 12px 0;
        }

        .dd-section-sublabel {
          font-size: 13px;
          color: #64748B;
          margin: 2px 0 0 0;
        }

        .dd-checkbox-row {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 14px;
          color: #1E293B;
        }

        .dd-checkbox {
          width: 18px;
          height: 18px;
          border-radius: 5px;
          border: 1.5px solid #CBD5E1;
          accent-color: #2563EB;
          cursor: pointer;
          flex-shrink: 0;
        }
        .dd-checkbox:disabled { opacity: 0.4; cursor: not-allowed; }

        .dd-btn-secondary {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 18px;
          background: #ffffff; border: 1px solid #E2E8F0; border-radius: 8px;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px; font-weight: 600; color: #0F172A;
          cursor: pointer; box-shadow: 0 1px 2px rgba(0,0,0,0.05);
          transition: border-color 0.15s, background 0.15s;
        }
        .dd-btn-secondary:hover { border-color: #94A3B8; background: #F8FAFC; }
        .dd-btn-secondary:disabled { opacity: 0.5; cursor: not-allowed; }

        .dd-btn-primary {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 18px;
          background: #2563EB; border: 1px solid #2563EB; border-radius: 8px;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px; font-weight: 600; color: #ffffff;
          cursor: pointer;
          transition: background 0.15s, box-shadow 0.15s;
        }
        .dd-btn-primary:hover { background: #1D4ED8; box-shadow: 0 4px 12px rgba(37,99,235,0.25); }
        .dd-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

        .dd-link-btn {
          background: none; border: none; padding: 0;
          font-family: 'DM Sans', sans-serif;
          font-size: 11px; font-weight: 600; color: #2563EB;
          cursor: pointer; text-decoration: underline;
        }
        .dd-link-btn:disabled { color: #94A3B8; cursor: not-allowed; text-decoration: none; }

        .dd-pm-badge {
          font-size: 11px;
          font-weight: 600;
          padding: 2px 8px;
          border-radius: 6px;
          border: 1px solid #E2E8F0;
          background: #F8FAFC;
          color: #475569;
          white-space: nowrap;
        }
        .dd-modal-row {
          display: flex; justify-content: space-between; align-items: center;
          gap: 16px; padding: 10px 0; border-bottom: 1px solid #F1F5F9;
          font-size: 13px;
        }
        .dd-modal-row:last-child { border-bottom: none; }
        .dd-modal-row dt { color: #64748B; margin: 0; }
        .dd-modal-row dd { color: #0F172A; font-weight: 600; margin: 0; text-align: right; }
        .dd-link-danger {
          background: none; border: none; padding: 0;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px; font-weight: 600; color: #DC2626; cursor: pointer;
        }
        .dd-link-danger:hover { text-decoration: underline; }
        .dd-link-danger:disabled { color: #94A3B8; cursor: not-allowed; text-decoration: none; }
        .dd-pm-badge--revolut {
          background: #191C1F;
          border-color: #191C1F;
          color: #ffffff;
        }
      `}</style>

      <div
        className="dd-root w-full overflow-visible"
        style={{ background: "rgb(241, 245, 249)", minHeight: "100vh" }}
      >
        <NavigationPanel />

        <div
          style={{
            position: "absolute",
            top: 136,
            width: "100%",
            padding: "0 96px 64px",
            display: "flex",
            flexDirection: "column",
            gap: 24,
          }}
        >
          {/* Page heading */}
          <div style={{ borderBottom: "1px solid #E2E8F0", paddingBottom: 24 }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#0F172A" }}>
              Settings
            </h1>
            <p style={{ margin: "4px 0 0", fontSize: 14, color: "#64748B" }}>
              Get your billing information, generate/review invoices.
            </p>
          </div>

          {/* Body: sidenav + content */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 32 }}>
            <SettingNav />

            <div
              className="dd-card"
              style={{ flex: 1, padding: 28, display: "flex", flexDirection: "column", gap: 24 }}
            >
              {/* Card header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  paddingBottom: 20,
                  borderBottom: "1px solid #E2E8F0",
                }}
              >
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#0F172A" }}>Billing</div>
                  <div style={{ fontSize: 13, color: "#64748B", marginTop: 2 }}>
                    Allow users to configure how and when communication is initiated with debtors.
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
                  <button className="dd-btn-secondary">Cancel</button>
                  <button className="dd-btn-primary" onClick={openToastMsg}>Save</button>
                </div>
              </div>

              {/* Plan details section */}
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <p className="dd-section-label" style={{ margin: 0 }}>Plan details</p>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 24,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontWeight: 700, fontSize: 16, color: "#0F172A" }}>
                        {subscriptionData?.company
                          ? getPlanName(subscriptionData.company.accountType)
                          : "Business plan"}
                      </span>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          padding: "2px 8px",
                          borderRadius: 6,
                          border: "1px solid #E2E8F0",
                          backgroundColor: "#F8FAFC",
                          color: "#475569",
                        }}
                      >
                        Monthly
                      </span>
                    </div>
                    <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748B" }}>
                      {subscriptionData?.company
                        ? getPlanDescription(subscriptionData.company.accountType)
                        : "Our most popular plan for small teams."}
                    </p>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: "#0F172A" }}>
                      {getDisplayPrice()}
                    </div>
                    <div style={{ fontSize: 13, color: "#64748B" }}>per month</div>
                  </div>
                </div>

                {/* User progress */}
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}>
                    {subscriptionData?.company
                      ? `${subscriptionData.company.usedLicenses} of ${subscriptionData.company.maxLicenses} users`
                      : "5 of 10 users"}
                  </div>
                  <div style={{ width: "100%", height: 8, backgroundColor: "#F1F5F9", borderRadius: 4 }}>
                    <div
                      style={{
                        width: subscriptionData?.company
                          ? `${(subscriptionData.company.usedLicenses / subscriptionData.company.maxLicenses) * 100}%`
                          : "50%",
                        height: "100%",
                        backgroundColor: "#2563EB",
                        borderRadius: 4,
                        transition: "width 0.3s ease",
                      }}
                    />
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: 8 }}>
                  <button
                    onClick={openManageModal}
                    className="dd-btn-secondary"
                    disabled={!subscriptionData?.hasSubscription}
                  >
                    Manage subscription
                  </button>
                </div>
              </div>

              <hr className="dd-section-divider" />

              {/* ── Subscription payment method (Stripe card / Revolut / Revolut Pay / Link) ── */}
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <p className="dd-section-label" style={{ margin: 0 }}>Subscription payment method</p>
                <p className="dd-section-sublabel">{pmDisplay.sublabel}</p>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginTop: 4,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 8,
                        border: pmDisplay.dark ? "1px solid #191C1F" : "1px solid #E2E8F0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: pmDisplay.dark ? "#191C1F" : "#F8FAFC",
                        overflow: "hidden",
                      }}
                    >
                      {pmDisplay.fallback && pmIconFailed ? (
                        <span style={{ color: pmDisplay.dark ? "#ffffff" : "#0F172A", fontWeight: 700, fontSize: 18, lineHeight: 1 }}>
                          {pmDisplay.fallback}
                        </span>
                      ) : (
                        <img
                          src={pmDisplay.icon}
                          alt={pmDisplay.iconAlt}
                          style={{ width: 24 }}
                          onError={() => pmDisplay.fallback && setPmIconFailed(true)}
                        />
                      )}
                    </div>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontWeight: 600, fontSize: 14, color: "#0F172A" }}>
                          {pmDisplay.title}
                        </span>
                        {pmDisplay.badge && pmDisplay.badge !== pmDisplay.title && (
                          <span className={`dd-pm-badge${pmDisplay.dark ? " dd-pm-badge--revolut" : ""}`}>
                            {pmDisplay.badge}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: TONE_COLORS[pmDisplay.tone] || "#64748B", marginTop: 2 }}>
                        {pmDisplay.subtitle}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={openManageModal}
                    className="dd-btn-secondary"
                    disabled={!subscriptionData?.hasSubscription}
                  >
                    Manage
                  </button>
                </div>
              </div>

              <hr className="dd-section-divider" />

              {/* ── Debt collection account (Stripe Connect) ── */}
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <p className="dd-section-label" style={{ margin: 0 }}>Debt collection account</p>
                <p className="dd-section-sublabel">
                  The Stripe Connect account that receives payments from your debtors when you send an LBA.
                </p>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginTop: 4,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 8,
                        border: "1px solid #E2E8F0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#F8FAFC",
                      }}
                    >
                      <img src="assets/stripe.svg" alt="Stripe" style={{ width: 24 }} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14, color: "#0F172A" }}>Stripe Connect</div>
                      <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>
                        Account:{" "}
                        <span style={{ fontWeight: 600, color: "#1E293B" }}>
                          {stripeAccountStatus?.connected && stripeAccountStatus?.accountId
                            ? `${stripeAccountStatus.accountId.substring(0, 20)}...`
                            : "Not connected"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#475569",
                      }}
                    >
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor:
                            stripeAccountStatus?.connected && stripeAccountStatus?.onboardingComplete
                              ? "#22C55E"
                              : "#94A3B8",
                          display: "inline-block",
                        }}
                      />
                      {stripeAccountStatus?.connected && stripeAccountStatus?.onboardingComplete
                        ? "Connected"
                        : "Not Connected"}
                    </div>
                    <button
                      onClick={handleStripeConnect}
                      className="dd-btn-secondary"
                      disabled={actionLoading}
                    >
                      {actionLoading
                        ? "Loading..."
                        : stripeAccountStatus?.connected && stripeAccountStatus?.onboardingComplete
                          ? "Open Dashboard"
                          : stripeAccountStatus?.connected
                            ? "Complete Setup"
                            : "Connect"}
                    </button>
                  </div>
                </div>
              </div>

              <hr className="dd-section-divider" />

             
            </div>
          </div>
          <Footer />
        </div>
      </div>

      {/* Manage Subscription Modal */}
      {isManageModalOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeManageModal}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="manage-sub-title"
            className="dd-root"
            style={{
              width: 460, maxWidth: "calc(100vw - 32px)", minHeight: 0,
              background: "#ffffff", borderRadius: 16, padding: 24,
              boxShadow: "0 10px 40px rgba(15,23,42,0.18)",
              display: "flex", flexDirection: "column", gap: 20,
            }}
          >
            <div>
              <div id="manage-sub-title" style={{ fontSize: 18, fontWeight: 700, color: "#0F172A" }}>
                Manage subscription
              </div>
              <div style={{ fontSize: 13, color: "#64748B", marginTop: 4 }}>
                Your plan, how it's billed, and what you can change.
              </div>
            </div>

            <dl style={{ margin: 0 }}>
              <div className="dd-modal-row">
                <dt>Plan</dt>
                <dd>{planName} · {getDisplayPrice()}/month</dd>
              </div>
              <div className="dd-modal-row">
                <dt>Status</dt>
                <dd>
                  {statusMeta ? (
                    <span
                      style={{
                        fontSize: 12, padding: "2px 8px", borderRadius: 999,
                        color: statusMeta.color, background: statusMeta.bg,
                      }}
                    >
                      {statusMeta.label}
                    </span>
                  ) : (
                    "—"
                  )}
                </dd>
              </div>
              <div className="dd-modal-row">
                <dt>Billed through</dt>
                <dd>{providerLabel}</dd>
              </div>
              <div className="dd-modal-row">
                <dt>Payment method</dt>
                <dd>{pmDisplay.title}</dd>
              </div>
              {nextPaymentDate && !isCancelled && (
                <div className="dd-modal-row">
                  <dt>Next payment</dt>
                  <dd>{formatDate(nextPaymentDate)}</dd>
                </div>
              )}
            </dl>

            {isRevolutSub ? (
              <div
                style={{
                  fontSize: 13, lineHeight: 1.5, color: "#334155",
                  background: "#F8FAFC", border: "1px solid #E2E8F0",
                  borderRadius: 10, padding: "12px 14px",
                }}
              >
                Revolut subscriptions don't have a self-service billing page. Your card is charged
                automatically each month. To use a different card, contact us and we'll send you a
                secure Revolut payment link.
                {SUPPORT_EMAIL && (
                  <div style={{ marginTop: 10 }}>
                    <a
                      className="dd-btn-secondary"
                      style={{ textDecoration: "none" }}
                      href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
                        `Change payment card - ${subscriptionData?.company?.name || ""}`
                      )}`}
                    >
                      Contact support
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <button
                  className="dd-btn-primary"
                  style={{ justifyContent: "center" }}
                  onClick={openBillingPortal}
                  disabled={portalLoading || isCancelled}
                >
                  {portalLoading ? `Opening ${providerLabel}…` : "Update payment method"}
                </button>
                <div style={{ fontSize: 12, color: "#64748B", textAlign: "center" }}>
                  Opens {providerLabel}'s secure billing page in this tab.
                </div>
              </div>
            )}

            {portalError && (
              <div
                role="alert"
                style={{
                  fontSize: 13, padding: "8px 12px", borderRadius: 8,
                  background: "#FEF2F2", color: "#B91C1C",
                }}
              >
                {portalError}
              </div>
            )}

            <div
              style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                paddingTop: 16, borderTop: "1px solid #E2E8F0",
              }}
            >
              <button
                className="dd-link-danger"
                onClick={startCancelFromManage}
                disabled={portalLoading || isCancelled}
              >
                Cancel subscription
              </button>
              <button className="dd-btn-secondary" onClick={closeManageModal} disabled={portalLoading}>
                Close
              </button>
            </div>
          </div>
        </PortalPopup>
      )}

      {/* Cancel Subscription Modal */}
      {isCancelModalOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeCancelModal}
        >
          <div className="w-[400px] bg-white rounded-lg shadow-lg p-6">
            <div className="flex flex-col gap-4">
              <div className="text-xl font-semibold text-neutrals-neutral-900">
                Cancel Subscription
              </div>
              <div className="text-sm text-neutrals-neutral-300">
                {isRevolutSub ? (
                  <>
                    Are you sure you want to cancel your subscription? Revolut subscriptions are
                    cancelled straight away, so access for you and your team will end immediately.
                  </>
                ) : (
                  <>
                    Are you sure you want to cancel your subscription? Your account will remain active
                    until the end of your current billing period on{" "}
                    {subscriptionData?.company?.nextBillingDate
                      ? formatDate(subscriptionData.company.nextBillingDate)
                      : "the end of the billing period"}
                    .
                  </>
                )}
              </div>
              <div className="flex gap-3 mt-4">
                <button className="dd-btn-secondary" onClick={closeCancelModal}>
                  Keep Subscription
                </button>
                <button
                  onClick={handleCancelSubscription}
                  disabled={actionLoading}
                  className="dd-btn-primary"
                  style={{ background: "#EF4444", borderColor: "#EF4444" }}
                >
                  {actionLoading ? "Canceling..." : "Cancel Subscription"}
                </button>
              </div>
            </div>
          </div>
        </PortalPopup>
      )}

      {isToastMsgOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeToastMsg}
        >
          <ToastMsg
            onClose={closeToastMsg}
            message={toastMessage || "Changes saved successfully!"}
          />
        </PortalPopup>
      )}

      {isLogoutConfirmationOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeLogoutConfirmation}
        >
          <LogoutConfirmation onClose={closeLogoutConfirmation} />
        </PortalPopup>
      )}

      {isExportModalTablesOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeExportModalTables}
        >
          <ExportModalTables onClose={closeExportModalTables} />
        </PortalPopup>
      )}

      {isNewPayment1Open && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeNewPayment1}
        >
          <NewPayment onClose={closeNewPayment1} />
        </PortalPopup>
      )}
    </>
  );
};

export default SettingsBillingPage;